import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;900&display=swap');

  @keyframes floatY {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-18px); }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.85); }
    to { opacity: 1; transform: scale(1); }
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes dashDraw {
    from { stroke-dashoffset: 600; }
    to { stroke-dashoffset: 0; }
  }

  .nf-root {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    font-family: 'Poppins', sans-serif;
 
    overflow: hidden;
    position: relative;
  }

  .nf-root::before {
    content: '';
    position: absolute;
    top: -80px;
    right: -80px;
    width: 340px;
    height: 340px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,95,31,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .nf-root::after {
    content: '';
    position: absolute;
    bottom: -60px;
    left: -60px;
    width: 260px;
    height: 260px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,95,31,0.06) 0%, transparent 70%);
    pointer-events: none;
  }

  .nf-card {
    max-width: 560px;
    width: 100%;
    text-align: center;
    animation: fadeInUp 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .nf-illustration {
    margin: 0 auto 36px;
    width: 220px;
    height: 220px;
    animation: floatY 4s ease-in-out infinite;
  }

  .nf-heading {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a1a2e;
    line-height: 1.2;
    margin: 0 0 14px;
    animation: fadeInUp 0.5s 0.3s both;
  }

  .nf-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
    animation: fadeInUp 0.5s 0.5s both;
  }

  .nf-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 13px 28px;
    background: #FF5F1F;
    color: #fff;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    font-family: 'Poppins', sans-serif;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 18px rgba(255,95,31,0.25);
  }

  .nf-btn-primary:hover {
    background: #e04e0f;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255,95,31,0.35);
    color: #fff;
    text-decoration: none;
  }

  .nf-btn-primary:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    .nf-heading { font-size: 1.6rem; }
    .nf-illustration { width: 170px; height: 170px; }
  }
`;

const IllustrationSVG = () => (
  <svg viewBox="0 0 220 220" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="110" cy="110" r="100" fill="#FFF3EE" />

    <rect x="52" y="88" width="116" height="90" rx="12" fill="#fff" stroke="#E5E7EB" strokeWidth="2" />

    <path
      d="M82 88 C82 68 138 68 138 88"
      stroke="#FF5F1F"
      strokeWidth="5"
      strokeLinecap="round"
      fill="none"
      strokeDasharray="600"
      strokeDashoffset="600"
      style={{ animation: "dashDraw 0.8s 0.3s ease forwards" }}
    />

    <text
      x="110"
      y="146"
      textAnchor="middle"
      fontFamily="Poppins, sans-serif"
      fontWeight="900"
      fontSize="36"
      fill="#FF5F1F"
      letterSpacing="-1"
    >
      404
    </text>

    <circle cx="93" cy="118" r="4" fill="#FF5F1F" opacity="0.3" />
    <circle cx="127" cy="118" r="4" fill="#FF5F1F" opacity="0.3" />
    <path d="M97 168 Q110 160 123 168" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" fill="none" />

    <circle cx="44" cy="72" r="5" fill="#FF5F1F" opacity="0.2" />
    <circle cx="176" cy="60" r="4" fill="#FF5F1F" opacity="0.15" />
    <circle cx="160" cy="170" r="3" fill="#FF5F1F" opacity="0.2" />
    <circle cx="52" cy="160" r="3.5" fill="#FF5F1F" opacity="0.15" />

    <path
      d="M170 45 L172 51 L178 51 L173 55 L175 61 L170 57 L165 61 L167 55 L162 51 L168 51 Z"
      fill="#FF5F1F"
      opacity="0.25"
    />
  </svg>
);

function NotFound() {
  const { languageCode } = useSelector((state) => state.auth);
  const isRTL = languageCode === "ar" || languageCode === "urdu";

  return (
    <>
      <style>{styles}</style>
      <div className="nf-root" dir={isRTL ? "rtl" : "ltr"}>
        <div className="nf-card">
          <div className="nf-illustration">
            <IllustrationSVG />
          </div>

          <h1 className="nf-heading">Oops! Page not found</h1>

          <div className="nf-actions">
            <Link to="/" className="nf-btn-primary">
              🛒 Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default NotFound;
